import { LivestreamRequester } from '@cobliteam/api-lib';

import type { LivestreamResponse } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { type QueryKey, queryOptions, type UseMutationOptions, type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const QUERY_KEY: QueryKey = ['CAMERA-LIVESTREAM'];

const env = Environment.getInstance().getEnvironment();
const getLivestreamRequester = () => new LivestreamRequester({ host: env.apiHost });

const getDeviceLivestreamOptions = (deviceId: string, options?: Partial<UseQueryOptions<LivestreamResponse>>) => {
  return queryOptions<LivestreamResponse>({
    queryKey: [...QUERY_KEY, deviceId],
    queryFn: () => getLivestreamRequester().getLivestream({ deviceId }),
    enabled: !!deviceId,
    ...options,
  });
};

const useCloseDeviceLivestream = (deviceId: string, options?: Partial<UseMutationOptions<void, Error, void>>) => {
  const queryClient = useQueryClient();

  return useMutation<void>({
    mutationKey: [...QUERY_KEY, deviceId, 'close'],
    mutationFn: () => getLivestreamRequester().closeLivestream({ deviceId }),
    onSuccess: () => {
      // Remove queries completely to ensure fresh data on reconnect
      queryClient.removeQueries({ queryKey: [...QUERY_KEY, deviceId] });
    },
    ...options,
  });
};

const useDeviceLivestream = () => {
  const useDeviceLivestreamQuery = (deviceId: string, options?: Partial<UseQueryOptions<LivestreamResponse>>) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<LivestreamResponse>({
      queryKey: [...QUERY_KEY, deviceId],
      queryFn: () => getLivestreamRequester().getLivestream({ deviceId }),
      ...options,
    });
  };

  return {
    useDeviceLivestreamQuery,
  };
};

export default useDeviceLivestream;

export { getDeviceLivestreamOptions, useCloseDeviceLivestream };
